import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { PermissionService } from '@core/services/permission.service';
import { LookupResponse } from '@core/models/common.model';
import { Permission } from '@shared/enums/permission.enum';
import { LookupSelectComponent } from '@shared/components/lookup-select/lookup-select.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { nullIfBlank } from '@shared/utils/payload-utils';
import { InterventionStore } from '../../../intervention.store';
import { ReferralPreferenceRequest, ReferralPreferenceResponse } from '../../../intervention.model';

// What the clinician and the member prefer for the referral (the "Referral Preferences" step): the kind of counselling, the language, the
// affiliate's gender and religion, the medium, the suburb, special needs and additional services. Internal staff edit them; the server uses
// them to rank the affiliates when the referral is issued, and keeps the criteria on the referral.
@Component({
  selector: 'referral-preference-panel',
  imports: [ReactiveFormsModule, LookupSelectComponent, ...SharedMaterialUIModule],
  templateUrl: './referral-preference-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReferralPreferencePanelComponent {
  protected readonly store = inject(InterventionStore);
  private readonly lookupStore = inject(LookupStore);
  private readonly permissionService = inject(PermissionService);
  private readonly formBuilder = inject(FormBuilder);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);

  protected readonly editing = signal(false);

  // The lists of the form, from the reference data.
  protected readonly counsellingTypes = computed(() => this.lookupStore.items(LOOKUP_CODES.counsellingType));
  protected readonly languages = computed(() => this.lookupStore.items(LOOKUP_CODES.language));
  protected readonly genders = computed(() => this.lookupStore.items(LOOKUP_CODES.gender));
  protected readonly religions = computed(() => this.lookupStore.items(LOOKUP_CODES.religion));
  protected readonly mediums = computed(() => this.lookupStore.items(LOOKUP_CODES.counsellingMedium));
  protected readonly specialNeedTypes = computed(() => this.lookupStore.items(LOOKUP_CODES.specialNeedType));
  protected readonly supportServiceTypes = computed(() => this.lookupStore.items(LOOKUP_CODES.supportServiceType));

  // The ticked special needs and additional services (the ids), with the detail typed for each.
  protected readonly chosenSpecialNeeds = signal<Record<string, string>>({});
  protected readonly chosenServices = signal<Record<string, string>>({});

  protected readonly canEdit = computed(
    () => this.permissionService.hasPermission(Permission.InterventionsUpdate) && this.interventionStatus() !== 'Closed',
  );

  protected readonly form = this.formBuilder.nonNullable.group({
    counsellingTypeId: [''],
    counsellingTypeOther: [''],
    preferredLanguageId: [''],
    preferredLanguageOther: [''],
    preferredAffiliateGenderId: [''],
    preferredAffiliateGenderOther: [''],
    preferredAffiliateReligionId: [''],
    preferredAffiliateReligionOther: [''],
    preferredCounsellingMediumId: [''],
    preferredSuburb: [''],
  });

  protected readonly isEmpty = computed(() => this.store.referralPreference()?.id == null);

  constructor() {
    this.lookupStore.load([
      LOOKUP_CODES.counsellingType,
      LOOKUP_CODES.language,
      LOOKUP_CODES.gender,
      LOOKUP_CODES.religion,
      LOOKUP_CODES.counsellingMedium,
      LOOKUP_CODES.specialNeedType,
      LOOKUP_CODES.supportServiceType,
    ]);

    // The server withholds the preferences from a provider until they accept the referral, so they are read again when the referrals change.
    effect(() => {
      this.store.referrals();
      this.store.loadReferralPreference(this.interventionId());
    });
  }

  protected edit(): void {
    const saved = this.store.referralPreference();
    this.form.reset({
      counsellingTypeId: saved?.counsellingTypeId ?? '',
      counsellingTypeOther: saved?.counsellingTypeOther ?? '',
      preferredLanguageId: saved?.preferredLanguageId ?? '',
      preferredLanguageOther: saved?.preferredLanguageOther ?? '',
      preferredAffiliateGenderId: saved?.preferredAffiliateGenderId ?? '',
      preferredAffiliateGenderOther: saved?.preferredAffiliateGenderOther ?? '',
      preferredAffiliateReligionId: saved?.preferredAffiliateReligionId ?? '',
      preferredAffiliateReligionOther: saved?.preferredAffiliateReligionOther ?? '',
      preferredCounsellingMediumId: saved?.preferredCounsellingMediumId ?? '',
      preferredSuburb: saved?.preferredSuburb ?? '',
    });
    this.chosenSpecialNeeds.set(Object.fromEntries((saved?.specialNeeds ?? []).map((x) => [x.specialNeedTypeId, x.otherDetail ?? ''])));
    this.chosenServices.set(Object.fromEntries((saved?.additionalServices ?? []).map((x) => [x.supportServiceTypeId, x.notes ?? ''])));
    this.editing.set(true);
  }

  protected cancel(): void {
    this.editing.set(false);
  }

  protected isSpecialNeedChosen(id: string): boolean {
    return id in this.chosenSpecialNeeds();
  }

  protected isServiceChosen(id: string): boolean {
    return id in this.chosenServices();
  }

  protected toggleSpecialNeed(id: string, checked: boolean): void {
    this.chosenSpecialNeeds.update((current) => this.toggled(current, id, checked));
  }

  protected toggleService(id: string, checked: boolean): void {
    this.chosenServices.update((current) => this.toggled(current, id, checked));
  }

  protected setSpecialNeedDetail(id: string, detail: string): void {
    this.chosenSpecialNeeds.update((current) => ({ ...current, [id]: detail }));
  }

  protected setServiceNotes(id: string, notes: string): void {
    this.chosenServices.update((current) => ({ ...current, [id]: notes }));
  }

  // An item that is not on the list of the chosen ones gets a text of its own ("Other" says what was meant).
  protected isOther(items: LookupResponse[], id: string): boolean {
    return items.find((item) => item.id === id)?.code === 'OTHER';
  }

  protected save(): void {
    const value = this.form.getRawValue();
    const request: ReferralPreferenceRequest = {
      counsellingTypeId: nullIfBlank(value.counsellingTypeId),
      counsellingTypeOther: nullIfBlank(value.counsellingTypeOther),
      preferredLanguageId: nullIfBlank(value.preferredLanguageId),
      preferredLanguageOther: nullIfBlank(value.preferredLanguageOther),
      preferredAffiliateGenderId: nullIfBlank(value.preferredAffiliateGenderId),
      preferredAffiliateGenderOther: nullIfBlank(value.preferredAffiliateGenderOther),
      preferredAffiliateReligionId: nullIfBlank(value.preferredAffiliateReligionId),
      preferredAffiliateReligionOther: nullIfBlank(value.preferredAffiliateReligionOther),
      preferredCounsellingMediumId: nullIfBlank(value.preferredCounsellingMediumId),
      preferredSuburb: nullIfBlank(value.preferredSuburb),
      specialNeeds: Object.entries(this.chosenSpecialNeeds()).map(([specialNeedTypeId, detail]) => ({
        specialNeedTypeId,
        otherDetail: nullIfBlank(detail),
      })),
      additionalServices: Object.entries(this.chosenServices()).map(([supportServiceTypeId, notes]) => ({
        supportServiceTypeId,
        notes: nullIfBlank(notes),
      })),
    };

    this.store.saveReferralPreference(this.interventionId(), request, () => this.editing.set(false));
  }

  // The name of a chosen list item, for the read-only view.
  protected nameOf(items: LookupResponse[], id: string | null): string {
    return (id && items.find((item) => item.id === id)?.name) || 'No preference';
  }

  protected summary(preference: ReferralPreferenceResponse | null): { label: string; value: string }[] {
    if (!preference) {
      return [];
    }

    return [
      { label: 'Counselling type', value: this.nameOf(this.counsellingTypes(), preference.counsellingTypeId) },
      { label: 'Language', value: this.nameOf(this.languages(), preference.preferredLanguageId) },
      { label: 'Affiliate gender', value: this.nameOf(this.genders(), preference.preferredAffiliateGenderId) },
      { label: 'Affiliate religion', value: this.nameOf(this.religions(), preference.preferredAffiliateReligionId) },
      { label: 'Medium', value: this.nameOf(this.mediums(), preference.preferredCounsellingMediumId) },
      { label: 'Suburb', value: preference.preferredSuburb || 'No preference' },
      { label: 'Special needs', value: preference.specialNeeds.map((x) => x.name ?? x.code).join(', ') || 'None' },
      { label: 'Additional services', value: preference.additionalServices.map((x) => x.name ?? x.code).join(', ') || 'None' },
    ];
  }

  private toggled(current: Record<string, string>, id: string, checked: boolean): Record<string, string> {
    const next = { ...current };
    if (checked) {
      next[id] = next[id] ?? '';
    } else {
      delete next[id];
    }
    return next;
  }
}
